'use client';

import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { T } from './V2Tokens';
import { PROCESS_STEPS } from './V1Data';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export function V2Canvas() {
    const isMobile  = useIsMobile();
    const outerRef  = useRef<HTMLDivElement>(null);
    const innerRef  = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        if (isMobile) return;

        const NUM = PROCESS_STEPS.length;

        // ── Set initial state explicitly so GSAP owns these properties ────
        // Panel 0 is visible; 1-3 start below and invisible
        for (let i = 1; i < NUM; i++) {
            gsap.set(`.v2-panel-${i}`, { y: '55%', opacity: 0 });
        }
        // Progress segments start collapsed (transformOrigin set in CSS)
        for (let i = 0; i < NUM; i++) {
            gsap.set(`.v2-prog-${i}`, { scaleX: 0 });
        }

        // ── Header entrance when section enters viewport ──────────────────
        gsap.fromTo(
            headerRef.current ? Array.from(headerRef.current.children) : [],
            { y: 20, opacity: 0 },
            {
                y: 0, opacity: 1,
                stagger: 0.1, duration: 0.65, ease: 'power3.out',
                scrollTrigger: { trigger: innerRef.current, start: 'top 85%' },
            }
        );

        // ── Scroll story: scrubbed timeline drives step-by-step reveal ────
        // The outer container is (NUM + 1) × 100vh tall.
        // The inner section is sticky. As the outer scrolls past, each step
        // activates in sequence — like scrubbing through a video.
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: outerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 1.5,
            },
        });

        // Initial hold at step 0
        tl.to({}, { duration: 0.7 });

        for (let i = 0; i < NUM - 1; i++) {
            const next = i + 1;

            // Progress segment for current step fills
            tl.to(`.v2-prog-${i}`, { scaleX: 1, duration: 0.4 });

            // Exit: slide up + fade — sequential exit before enter prevents overlap
            tl.to(`.v2-panel-${i}`, { y: '-32%', opacity: 0, duration: 0.38, ease: 'power2.in' }, '<0.08');

            // Enter: starts after exit completes (no '<' offset = strictly sequential)
            tl.to(`.v2-panel-${next}`, { y: 0, opacity: 1, duration: 0.48, ease: 'power3.out' });

            // Hold at this step
            tl.to({}, { duration: 1.1 });
        }

        // Fill last progress segment
        tl.to(`.v2-prog-${NUM - 1}`, { scaleX: 1, duration: 0.5 });
        tl.to({}, { duration: 0.4 });

    }, { scope: outerRef, dependencies: [isMobile] });

    if (isMobile) {
        return (
            <section
                id="process"
                style={{
                    padding: '48px 20px',
                    background: T.bgWhite,
                    borderRadius: '28px 28px 0 0',
                }}
                dir="rtl"
            >
                {/* Section header */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', borderRadius: T.rPill,
                        background: T.bgCard, border: `1px solid ${T.line}`,
                        fontSize: 12, fontWeight: 600, color: T.ink70,
                        marginBottom: 18,
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                        آلية العمل
                    </span>
                    <h2 style={{
                        fontSize: 'clamp(28px, 7vw, 40px)',
                        fontWeight: 900, lineHeight: 1.08, margin: '0 0 10px',
                        letterSpacing: '-0.025em', color: T.ink,
                    }}>
                        من أول اجتماع إلى <span style={{ color: T.brand }}>نظام نمو</span> يعمل وحده.
                    </h2>
                </div>

                {/* Step cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {PROCESS_STEPS.map((s, i) => {
                        const isLast = i === PROCESS_STEPS.length - 1;
                        return (
                            <div
                                key={i}
                                style={{
                                    borderRadius: 20,
                                    background: T.bgCard,
                                    border: `1px solid ${isLast ? T.brand : T.line}`,
                                    padding: '24px 20px',
                                }}
                            >
                                <div style={{ fontSize: 11, fontWeight: 600, color: T.ink50, letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: 10 }}>
                                    {s.n} · {s.en}
                                </div>
                                <h3 style={{
                                    fontSize: 'clamp(22px, 5.5vw, 28px)',
                                    fontWeight: 900, lineHeight: 1.1,
                                    letterSpacing: '-0.03em', color: isLast ? T.brand : T.ink,
                                    margin: '0 0 10px',
                                }}>
                                    {s.ar}
                                </h3>
                                <p style={{ fontSize: 14, color: T.ink70, lineHeight: 1.7, margin: 0 }}>
                                    {s.d}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    }

    return (
        /* Outer: tall container = (steps + 1) viewport heights of scroll space */
        <div ref={outerRef} style={{ height: `${PROCESS_STEPS.length * 110}vh`, position: 'relative' }}>

            {/* Inner: sticky — pins at viewport top for the whole scroll story */}
            <section
                ref={innerRef}
                id="process"
                style={{
                    position: 'sticky',
                    top: 64,
                    height: 'calc(100vh - 64px)',
                    overflow: 'hidden',
                    background: T.bgWhite,
                    borderRadius: '28px 28px 0 0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    paddingTop: isMobile ? 28 : 40,
                    paddingLeft: isMobile ? 20 : 56,
                    paddingRight: isMobile ? 20 : 56,
                    boxShadow: '0 -20px 60px rgba(14,11,46,0.07)',
                }}
            >
                {/* Section header */}
                <div ref={headerRef} style={{ textAlign: 'center', marginBottom: 24 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', borderRadius: T.rPill,
                        background: T.bgCard, border: `1px solid ${T.line}`,
                        fontSize: 12, fontWeight: 600, color: T.ink70,
                        marginBottom: 18,
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                        آلية العمل
                    </span>
                    <h2 style={{
                        fontSize: 'clamp(30px, 3.6vw, 52px)',
                        fontWeight: 900, lineHeight: 1.08, margin: '0 0 10px',
                        letterSpacing: '-0.025em', color: T.ink,
                    }}>
                        من أول اجتماع إلى <span style={{ color: T.brand }}>نظام نمو</span> يعمل وحده.
                    </h2>
                    <p style={{ fontSize: 15, color: T.ink50, margin: 0 }}>
                        مرّر للاستكشاف — أربع مراحل، كل مرحلة قصة.
                    </p>
                </div>

                {/* Progress bar: 4 segments fill in order with scroll */}
                <div style={{ display: 'flex', gap: 8, maxWidth: 520, margin: '0 auto 28px' }}>
                    {PROCESS_STEPS.map((_, i) => (
                        <div key={i} style={{
                            flex: 1, height: 4, borderRadius: 2,
                            background: 'rgba(91,79,229,0.14)', overflow: 'hidden',
                        }}>
                            <div
                                className={`v2-prog-${i}`}
                                style={{
                                    width: '100%', height: '100%',
                                    background: T.brand, borderRadius: 2,
                                    transformOrigin: 'right center',
                                    /* Initial scaleX set by gsap.set in useGSAP */
                                }}
                            />
                        </div>
                    ))}
                </div>

                {/* Step panels: stacked absolutely, each slides in/out on scroll */}
                <div style={{ position: 'relative', flex: 1, maxWidth: 1100, margin: '0 auto', width: '100%', overflow: 'hidden' }}>
                    {PROCESS_STEPS.map((s, i) => {
                        const isLast = i === PROCESS_STEPS.length - 1;
                        return (
                            <div
                                key={i}
                                className={`v2-panel-${i}`}
                                style={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'flex-start',
                                    paddingTop: isMobile ? 16 : 32,
                                    gap: isMobile ? 0 : 64,
                                }}
                            >
                                {/* Step number — large, right side in RTL — hidden on mobile */}
                                <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 130, display: isMobile ? 'none' : undefined }}>
                                    <div style={{
                                        fontSize: 'clamp(64px, 9vw, 120px)',
                                        fontWeight: 900,
                                        lineHeight: 1,
                                        letterSpacing: '-0.06em',
                                        color: isLast ? T.brand : T.ink,
                                        opacity: isLast ? 1 : 0.09,
                                    }}>
                                        {s.n}
                                    </div>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: '50%',
                                        background: isLast ? T.brand : T.ink,
                                        margin: '10px auto 0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: isLast ? `0 8px 24px ${T.brandGlow}` : 'none',
                                    }}>
                                        <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
                                            <path d="M2.5 7l3 3 6-6" stroke="#FAF8FF" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Step content — left side in RTL */}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 600, color: T.ink50,
                                        letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: 14,
                                    }}>
                                        {s.en}
                                    </div>
                                    <h3 style={{
                                        fontSize: isMobile ? 'clamp(26px, 7vw, 36px)' : 'clamp(32px, 4vw, 58px)',
                                        fontWeight: 900,
                                        lineHeight: 1.08,
                                        letterSpacing: '-0.03em',
                                        color: T.ink,
                                        margin: '0 0 18px',
                                    }}>
                                        {s.ar}
                                    </h3>
                                    <p style={{
                                        fontSize: isMobile ? 15 : 17,
                                        color: T.ink70,
                                        lineHeight: 1.75,
                                        maxWidth: 540,
                                        margin: 0,
                                    }}>
                                        {s.d}
                                    </p>

                                    {/* Step dots — show which step is active */}
                                    <div style={{ display: 'flex', gap: 8, marginTop: 32, alignItems: 'center' }}>
                                        {PROCESS_STEPS.map((_, j) => (
                                            <div key={j} style={{
                                                height: 6,
                                                width: j === i ? 28 : 6,
                                                borderRadius: 3,
                                                background: j === i ? T.brand : T.brand100,
                                            }} />
                                        ))}
                                        <span style={{ fontSize: 12, color: T.ink50, marginRight: 4 }}>
                                            {i + 1} / {PROCESS_STEPS.length}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
